import { debounce } from 'lodash'
import { forwardRef, useCallback, useMemo, useRef } from 'react'
import { MultiValue, SingleValue } from 'react-select'
import AsyncSelect from 'react-select/async'
import Select from 'react-select/dist/declarations/src/Select'

import { SelectOption } from '@/components'
import {
	isEditableElement,
	isTextEditingKey,
} from '@/hooks/useKeyboardNavigation'
import {
	CustomMenuList,
	DefaultInputWithTestId,
	getSelectValue,
} from '@/lib/components/Select/CustomSelectComponents'
import { getAsyncSelectStyles } from '@/lib/components/Select/EnhancedAsyncSelect/styles'
import { getStylesConfig } from '@/lib/components/Select/EnhancedSyncSelect/styles'

import { ensureWronglyTypedLegacyCodeWorks, isMultiOption } from '../utils'
import { EnhancedAsyncSelectProps } from './types'

const DEBOUNCE_DELAY = 300

export const EnhancedAsyncSelect = forwardRef<
	Select<SelectOption, boolean>,
	EnhancedAsyncSelectProps
>(
	(
		{
			isMulti,
			onChange,
			options,
			value,
			valueKey = 'value',
			labelKey = 'label',
			useBaseStyles,
			testId,
			loadOptions: customLoadOptions,
			formatOptionLabel,
			components: customComponents,
			isTableCell,
			...props
		},
		ref,
	) => {
		// Track selected options for async mode where options array might be empty
		const selectedOptionsRef = useRef<Map<string, SelectOption>>(new Map())

		const defaultLoadOptions = useCallback(
			async (inputValue: string): Promise<SelectOption[]> => {
				if (!options) {
					return []
				}

				return options.filter((option) => {
					return (
						inputValue === '' ||
						option[labelKey]
							.toString()
							.toLowerCase()
							.includes(inputValue.toLowerCase())
					)
				})
			},
			[options, labelKey],
		)

		const baseLoadOptions = customLoadOptions ?? defaultLoadOptions

		// Debounced loadOptions for better UX (skip debounce for initial load)
		const debouncedLoadOptions = useMemo(
			() =>
				debounce(
					(inputValue: string, callback: (options: SelectOption[]) => void) => {
						baseLoadOptions(inputValue).then(callback)
					},
					DEBOUNCE_DELAY,
				),
			[baseLoadOptions],
		)

		const loadOptions = useCallback(
			(inputValue: string): Promise<SelectOption[]> => {
				// Skip debounce for initial load (empty input) to show options immediately
				if (!inputValue) {
					return baseLoadOptions(inputValue)
				}
				return new Promise((resolve) => {
					debouncedLoadOptions(inputValue, resolve)
				})
			},
			[baseLoadOptions, debouncedLoadOptions],
		)

		const getOptionValue = useCallback(
			(option: SelectOption): string => {
				const rawValue = option[valueKey]

				if (!rawValue) {
					return ''
				}

				return ensureWronglyTypedLegacyCodeWorks(rawValue)
			},
			[valueKey],
		)

		const getOptionLabel = useCallback(
			(option: SelectOption): string => {
				const label = option?.[labelKey]
				return label?.toString() ?? ''
			},
			[labelKey],
		)

		const handleChange = useCallback(
			(newValue: MultiValue<SelectOption> | SingleValue<SelectOption>) => {
				if (!onChange) return

				if (isMultiOption(newValue)) {
					newValue?.forEach((option) => {
						selectedOptionsRef.current.set(getOptionValue(option), option)
					})
					onChange(
						newValue ? newValue.map((option) => getOptionValue(option)) : [],
					)
				} else {
					if (newValue) {
						selectedOptionsRef.current.set(getOptionValue(newValue), newValue)
						onChange(getOptionValue(newValue))
					} else {
						onChange('')
					}
				}
			},
			[onChange, getOptionValue],
		)

		const selectValue = useMemo(() => {
			const fromOptions = getSelectValue(getOptionValue, options, value, isMulti)
			if (fromOptions) return fromOptions

			if (value && !isMulti && (typeof value === 'string' || typeof value === 'number')) {
				const valueStr = ensureWronglyTypedLegacyCodeWorks(value)
				const cached = selectedOptionsRef.current.get(valueStr)
				if (cached) return cached
			}

			return null
		}, [options, value, isMulti, getOptionValue])

		// Use options as defaultOptions - don't auto-load on mount to avoid double calls
		const effectiveDefaultOptions = options ?? []

		const mergedComponents = useMemo(
			() => ({
				MenuList: CustomMenuList,
				Input: DefaultInputWithTestId,
				...customComponents,
			}),
			[customComponents],
		)

		const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
			if (isEditableElement(e.target) && isTextEditingKey(e.key)) {
				e.stopPropagation()
			}
		}

		return (
			<div
				data-testid={`select-container-${testId}`}
				onKeyDown={handleKeyDown}
				role="listbox"
			>
				<AsyncSelect
					cacheOptions
					classNamePrefix="react-select"
					components={mergedComponents}
					defaultOptions={effectiveDefaultOptions}
					getOptionLabel={getOptionLabel}
					getOptionValue={getOptionValue}
					isMulti={isMulti}
					loadOptions={loadOptions}
					onChange={handleChange}
					ref={ref}
					styles={useBaseStyles ? getStylesConfig({ isTableCell }) : getAsyncSelectStyles()}
					placeholder="Select or search..."
					value={selectValue}
					data-testid={testId}
					formatOptionLabel={formatOptionLabel}
					{...props}
					isClearable
				/>
			</div>
		)
	},
)
